import apiClient from './apiClient';

class SearchService {
  /**
   * Search products (prescription medicines)
   * @param {Object} params - Search parameters
   * @param {string} params.search - General search term
   * @param {number} params.page - Page number for pagination
   * @param {string} params.first_letter - Filter by first letter
   * @param {string} params.salt_composition - Exact salt composition match
   */
  async searchProducts(params = {}) {
    try {
      console.log('SearchService: searchProducts called with params:', params);
      const queryParams = new URLSearchParams();
      
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.first_letter) queryParams.append('first_letter', params.first_letter);
      if (params.salt_composition) queryParams.append('salt_composition', params.salt_composition);

      const url = `/product/?${queryParams.toString()}`;
      console.log('SearchService: Making request to:', url);
      
      const response = await apiClient.get(url);
      console.log('SearchService: searchProducts response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  }

  /**
   * Search OTC items
   * @param {Object} params - Search parameters
   * @param {string} params.search - General search term
   * @param {number} params.page - Page number for pagination
   * @param {string} params.name - Filter by product name
   * @param {string} params.category - Filter by category
   * @param {string} params.manufacturer - Filter by manufacturer
   * @param {string} params.sub_category - Filter by sub-category
   */
  async searchOTC(params = {}) {
    try {
      console.log('SearchService: searchOTC called with params:', params);
      const queryParams = new URLSearchParams();
      
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.name) queryParams.append('name', params.name);
      if (params.category) queryParams.append('category', params.category);
      if (params.manufacturer) queryParams.append('manufacturer', params.manufacturer);
      if (params.sub_category) queryParams.append('sub_category', params.sub_category);

      const url = `/otc/?${queryParams.toString()}`;
      console.log('SearchService: Making request to:', url);
      
      const response = await apiClient.get(url);
      console.log('SearchService: searchOTC response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error searching OTC items:', error);
      throw error;
    }
  }

  /**
   * Combined search for both products and OTC items
   * @param {string} searchTerm - Search term
   * @param {number} page - Page number
   */
  async searchAll(searchTerm, page = 1) {
    try {
      const [productsResponse, otcResponse] = await Promise.all([
        this.searchProducts({ search: searchTerm, page }),
        this.searchOTC({ search: searchTerm, page })
      ]);

      return {
        products: productsResponse.results || [],
        otc: otcResponse.results || [],
        totalResults: (productsResponse.count || 0) + (otcResponse.count || 0),
        productsCount: productsResponse.count || 0,
        otcCount: otcResponse.count || 0,
        productsPages: productsResponse.num_pages || 0,
        otcPages: otcResponse.num_pages || 0,
        currentPage: page
      };
    } catch (error) {
      console.error('Error in combined search:', error);
      throw error;
    }
  }

  /**
   * Get search suggestions for autocomplete
   * @param {string} searchTerm - Search term
   */
  async getSearchSuggestions(searchTerm) {
    try {
      console.log('SearchService: getSearchSuggestions called with:', searchTerm);
      
      if (searchTerm.length < 2) {
        console.log('SearchService: search term too short, returning empty array');
        return [];
      }

      console.log('SearchService: Making API calls...');
      const [productsResponse, otcResponse] = await Promise.all([
        this.searchProducts({ search: searchTerm, page: 1 }),
        this.searchOTC({ search: searchTerm, page: 1 })
      ]);
      
      console.log('SearchService: API responses received:', { productsResponse, otcResponse });

      const suggestions = [];
      
      // Add product suggestions
      if (productsResponse.results) {
        productsResponse.results.slice(0, 5).forEach(product => {
          suggestions.push({
            id: product.id,
            name: product.name,
            type: 'product',
            manufacturer: product.manufacturer,
            salt_composition: product.salt_composition,
            price: product.price,
            prescription_required: product.prescription_required
          });
        });
      }

      // Add OTC suggestions
      if (otcResponse.results) {
        otcResponse.results.slice(0, 5).forEach(otc => {
          suggestions.push({
            id: otc.id,
            name: otc.name,
            type: 'otc',
            category: otc.category,
            manufacturer: otc.manufacturer,
            price: otc.price,
            prescription_required: otc.prescription_required
          });
        });
      }

      return suggestions;
    } catch (error) {
      console.error('Error getting search suggestions:', error);
      return [];
    }
  }

  /**
   * Advanced filtering for products
   * @param {Object} filters - Filter criteria
   */
  async filterProducts(filters) {
    try {
      const response = await apiClient.post('/product/filter/', filters);
      return response.data;
    } catch (error) {
      console.error('Error filtering products:', error);
      throw error;
    }
  }

  /**
   * Advanced filtering for OTC items
   * @param {Object} filters - Filter criteria
   */
  async filterOTC(filters) {
    try {
      const response = await apiClient.post('/otc/filter/', filters);
      return response.data;
    } catch (error) {
      console.error('Error filtering OTC items:', error);
      throw error;
    }
  }

  /**
   * Get OTC categories
   */
  async getOTCCategories() {
    try {
      const response = await apiClient.get('/otc/categories/');
      return response.data;
    } catch (error) {
      console.error('Error getting OTC categories:', error);
      throw error;
    }
  }

  /**
   * Get OTC manufacturers
   * @param {Array} categories - Categories to filter by
   */
  async getOTCManufacturers(categories = []) {
    try {
      const response = await apiClient.post('/otc/manufacturers/', { categories });
      return response.data;
    } catch (error) {
      console.error('Error getting OTC manufacturers:', error);
      throw error;
    }
  }

  /**
   * Get OTC sub-categories
   * @param {Array} categories - Categories to filter by
   */
  async getOTCSubCategories(categories = []) {
    try {
      const response = await apiClient.post('/otc/sub_categories/', { category: categories });
      return response.data;
    } catch (error) {
      console.error('Error getting OTC sub-categories:', error);
      throw error;
    }
  }
}

export default new SearchService();
